import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { produce } from 'immer';

import { bankFeedApi } from '@/domain/bankFeed/api';
import { showApiErrorToast, type EndpointBody, type EndpointResponse } from '@/services/api';
import { queryOptions, useApiMutation } from '@/services/query';
import { handleErrorResponseWithToast, yieldSuccessNotification } from '@/lib/toasts';
import { bankFeedKeys } from '@/domain/bankFeed/queries/keys';

export type ConnectionsCache = EndpointResponse<typeof bankFeedApi.getConnections>;

export const bankFeedConnectionsQuery = (companyId: string) =>
  queryOptions({
    queryKey: bankFeedKeys.connections(companyId),
    queryFn: () => bankFeedApi.getConnections(),
  });

export const useBankFeedConnections = (companyId: string) => useQuery(bankFeedConnectionsQuery(companyId));

export const bankFeedConnectionsLimitQuery = (companyId: string) =>
  queryOptions({
    queryKey: bankFeedKeys.connectionsLimit(companyId),
    queryFn: () => bankFeedApi.getConnectionsLimit(),
  });

export const useBankFeedConnectionsLimit = (companyId: string) =>
  useQuery({ ...bankFeedConnectionsLimitQuery(companyId), refetchOnMount: false });

export const useRequestMoreBankFeedConnectionsMutation = () =>
  useApiMutation({
    mutationFn: bankFeedApi.createSupportTicket,
    onSuccess: () => {
      yieldSuccessNotification({
        id: 'modal.bankFeed.connectionsLimit.request.success',
        defaultMessage: "Request sent. We'll get back to you soon.",
      });
    },
    onError: (error) =>
      showApiErrorToast(error, {
        id: 'app.selector.inquiryError',
        defaultMessage: 'We encountered a temporary error while sending your inquiry.',
      }),
  });

export const useDeleteBankFeedConnectionMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.deleteConnection,
    onSuccess: (_, { path }) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.filter((row) => row.id !== path.id);
        }),
      );
      qc.invalidateQueries(bankFeedKeys.connectionsLimit(companyId));
      yieldSuccessNotification({
        id: 'configurations.company.bankFeed.tabs.connections.toast.connection.delete.success',
        defaultMessage: 'Connection deleted successfully!',
      });
    },
    // Wrapped, not passed directly: onError is called as (error, variables, …)
    // and this helper's 2nd parameter is its options object, so a mutation that
    // sends a `path` would override the 'data.message' lookup and silence the
    // toast (same mistake as DPM-533 in beneficiaries.ts).
    onError: (error) => handleErrorResponseWithToast(error),
  });
};

export const useUpdateBankFeedConnectionMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.updateConnection,
    onSuccess: (updatedRow, { body }) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.map((row) => (row.id === updatedRow.id ? updatedRow : row));
        }),
      );
      yieldSuccessNotification(
        body?.suspended
          ? {
              id: 'configurations.company.bankFeed.tabs.connections.toast.connection.suspend.success',
              defaultMessage: 'Connection suspended successfully!',
            }
          : {
              id: 'configurations.company.bankFeed.tabs.connections.toast.connection.unsuspend.success',
              defaultMessage: 'Connection unsuspended successfully!',
            },
      );
    },
    onError: (error) => handleErrorResponseWithToast(error),
  });
};

// Create returns { connection_url, connection } — connection_url drives the
// bank-auth iframe (consumer reads it from mutation.data on success). The new
// row is NOT written into the connections cache here: it arrives via the
// WebSocket batch processor → getConnectionsByIds → setQueryData (see
// pages/.../Connections/sagas.ts). No global onError either — Step3 owns the
// error UI for the bank-auth flow.
//
// Per-call error type override (per the constitution in src/services/query/CLAUDE.md):
// Step3 reads `error.response.data.message` for the localized banner, so we
// narrow the axios payload to that shape here instead of casting at the use site.
type CreateConnectionError = AxiosError<{ message?: string }>;

export const useCreateBankFeedConnectionMutation = () =>
  useMutation<
    EndpointResponse<typeof bankFeedApi.createConnection>,
    CreateConnectionError,
    { body: EndpointBody<typeof bankFeedApi.createConnection> }
  >({ mutationFn: bankFeedApi.createConnection });

// Create-and-delegate in one write, also used by "Resend authorization link".
// No cache writes — the created connection arrives via the BANK_CONNECTION_*
// WebSocket events. Toasts live here so both dialogs share them.
export const useDelegateBankFeedConnectionMutation = () =>
  useApiMutation({
    mutationFn: bankFeedApi.delegateConnection,
    onSuccess: (_data, { body }) => {
      yieldSuccessNotification(
        body.channel === 'EMAIL'
          ? {
              id: 'modal.bankFeed.connection.add.delegate.toast.email',
              defaultMessage: 'Email was sent successfully!',
            }
          : {
              id: 'modal.bankFeed.connection.add.delegate.toast.whatsapp',
              defaultMessage: 'WhatsApp message was sent successfully!',
            },
      );
    },
    onError: (error) =>
      handleErrorResponseWithToast(error, {
        defaultMessage: {
          id: 'modal.bankFeed.connection.add.createError',
          defaultMessage: 'Something went wrong. Please try again.',
        },
      }),
  });

export const useSyncBankFeedTransactionsMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.syncTransactions,
    onSuccess: (updatedRow) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          draft.items = draft.items.map((row) => (row.id === updatedRow.id ? updatedRow : row));
        }),
      );
      yieldSuccessNotification({
        id: 'configurations.company.bankFeed.tabs.connections.toast.syncTransactions.success',
        defaultMessage: 'Bank transaction synchronization has started.',
      });
    },
    onError: (error) => handleErrorResponseWithToast(error),
  });
};

// ── Direct bank login ──────────────────────────────────────────────────────
// The sign-in happens in a browser we drive, so the UI learns what is going on
// by polling: RUNNING while it works, OTP_REQUIRED when the bank texts a code,
// ACTIVE once it is in. Polling stops the moment it reaches a resting state.

export const useCreateLegacyBankingConnectionMutation = () =>
  useMutation<
    EndpointResponse<typeof bankFeedApi.createLegacyBankingConnection>,
    AxiosError<{ message?: string }>,
    { body: EndpointBody<typeof bankFeedApi.createLegacyBankingConnection> }
  >({ mutationFn: bankFeedApi.createLegacyBankingConnection });

export const useReauthLegacyBankingConnectionMutation = () =>
  useMutation<
    EndpointResponse<typeof bankFeedApi.reauthLegacyBankingConnection>,
    AxiosError<{ message?: string }>,
    {
      path: { id: string };
      body: EndpointBody<typeof bankFeedApi.reauthLegacyBankingConnection>;
    }
  >({ mutationFn: bankFeedApi.reauthLegacyBankingConnection });

export const useSubmitLegacyBankingOtpMutation = () =>
  useMutation<
    EndpointResponse<typeof bankFeedApi.submitLegacyBankingOtp>,
    AxiosError<{ message?: string }>,
    { path: { id: string }; body: EndpointBody<typeof bankFeedApi.submitLegacyBankingOtp> }
  >({ mutationFn: bankFeedApi.submitLegacyBankingOtp });

export const useAssignLegacyBankingAccountsMutation = () =>
  useMutation<
    EndpointResponse<typeof bankFeedApi.assignLegacyBankingAccounts>,
    AxiosError<{ message?: string }>,
    { path: { id: string }; body: EndpointBody<typeof bankFeedApi.assignLegacyBankingAccounts> }
  >({ mutationFn: bankFeedApi.assignLegacyBankingAccounts });

// What the sign-in found. Only readable while the enrolment is parked on them —
// assigning clears the list, so this is not cached beyond the dialog.
export const useLegacyBankingDiscoveredAccounts = (connectionId: string | null) =>
  useQuery({
    queryKey: bankFeedKeys.legacyBankingDiscoveredAccounts(connectionId ?? ''),
    queryFn: () => bankFeedApi.getLegacyBankingDiscoveredAccounts({ path: { id: connectionId as string } }),
    enabled: Boolean(connectionId),
    cacheTime: 0,
  });

// What the login can see now, including accounts already taken by another company.
// Always fresh: a sync between openings changes who holds what.
export const useLegacyBankingLoginAccounts = (connectionId: string | null) =>
  useQuery({
    queryKey: bankFeedKeys.legacyBankingLoginAccounts(connectionId ?? ''),
    queryFn: () => bankFeedApi.getLegacyBankingLoginAccounts({ path: { id: connectionId as string } }),
    enabled: Boolean(connectionId),
    cacheTime: 0,
  });

// Which companies the user may hand an account to. Asked of the server rather than
// filtered here: a ghost user's reachable companies are not something the browser
// can be trusted to work out.
export const useLegacyBankingLinkableCompanies = (companyId: string, enabled: boolean) =>
  useQuery({
    queryKey: bankFeedKeys.legacyBankingLinkableCompanies(companyId),
    queryFn: () => bankFeedApi.getLegacyBankingLinkableCompanies(),
    enabled,
  });

// A login takes seconds, not milliseconds; this is frequent enough to feel
// immediate without flooding a browser that is busy driving a bank site.
const LEGACY_BANKING_POLL_INTERVAL_MS = 2000;

export const useLegacyBankingConnectionState = (connectionId: string | null) =>
  useQuery({
    queryKey: bankFeedKeys.legacyBankingConnection(connectionId ?? ''),
    queryFn: () => bankFeedApi.getLegacyBankingConnection({ path: { id: connectionId as string } }),
    enabled: Boolean(connectionId),
    refetchInterval: (data, query) =>
      // Nothing left to wait for once it is in, failed, asking for a code, or
      // holding accounts to be chosen — that last one is still INACTIVE, and
      // without it the dialog would poll a finished sign-in forever. A failed read
      // stops it too: a sign-in the bank refused is deleted, and its id never answers.
      !query.state.error && (!data || (data.status === 'INACTIVE' && !data.accounts_pending))
        ? LEGACY_BANKING_POLL_INTERVAL_MS
        : false,
  });
